import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
  alpha,
  CircularProgress,
  Button,
  Tooltip,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { KnowledgehubFileResponse } from "../../../../app/models/responseModels/knowledgehubResponse";
import { getFileConfig } from "../constants/knowledgehubConstants";
import { KnowledgehubAgent } from "../../../../app/api/knowledgehub/KnowledgehubAgent";
import { toast } from "react-toastify";

type Props = {
  file: KnowledgehubFileResponse | null;
  onClose: () => void;
};

type FetchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; url: string }
  | { status: "error"; message: string };

const VIEWER_HEIGHT = 620;
const GOOGLE_DOCS_VIEWER_BASE = "https://docs.google.com/viewer";
const SUPPORTED_GOOGLE_VIEWER_TYPES = new Set(["PDF", "WORD", "PPT"]);

function buildGoogleDocsViewerUrl(signedUrl: string): string {
  return `${GOOGLE_DOCS_VIEWER_BASE}?url=${encodeURIComponent(signedUrl)}&embedded=true`;
}


function LoadingState() {
  return (
    <Box
      role="status"
      aria-label="Loading file"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        height: VIEWER_HEIGHT,
        bgcolor: "#000",
      }}
    >
      <CircularProgress size={36} sx={{ color: "grey.400" }} />
      <Typography variant="caption" color="grey.500">
        Loading file…
      </Typography>
    </Box>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Box
      role="alert"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        height: VIEWER_HEIGHT,
        bgcolor: "#fbfbfb",
        px: 3,
      }}
    >
      <ErrorOutlineIcon sx={{ color: "error.light", fontSize: 40 }} />
      <Typography color="grey.400" variant="body2" textAlign="center">
        {message}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={onRetry}
        sx={{
          color: "grey.300",
          borderColor: "grey.700",
          "&:hover": { borderColor: "grey.400", bgcolor: alpha("#fff", 0.05) },
        }}
      >
        Retry
      </Button>
    </Box>
  );
}

// function OverlayButton({
//   label,
//   icon,
//   onClick,
// }: {
//   label: string;
//   icon: React.ReactNode;
//   onClick: () => void;
// }) {
//   return (
//     <Tooltip title={label} placement="left">
//       <Button
//         size="small"
//         startIcon={icon}
//         onClick={onClick}
//         aria-label={label}
//         sx={{
//           position: "absolute",
//           top: 10,
//           right: 10,
//           zIndex: 10,
//           bgcolor: alpha("#000", 0.65),
//           color: "grey.200",
//           backdropFilter: "blur(4px)",
//           fontSize: "0.7rem",
//           px: 1.25,
//           "&:hover": {
//             bgcolor: alpha("#000", 0.85),
//             color: "white",
//           },
//         }}
//       >
//         {label}
//       </Button>
//     </Tooltip>
//   );
// }

function GoogleDocsViewer({
  signedUrl,
  title,
  fallbackLabel,
  onFallback,
}: {
  signedUrl: string;
  title: string;
  fallbackLabel: string;
  onFallback: () => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const viewerUrl = buildGoogleDocsViewerUrl(signedUrl);

  return (
    <Box sx={{ position: "relative", height: VIEWER_HEIGHT }}>
      <iframe
        ref={iframeRef}
        src={viewerUrl}
        title={title}
        width="100%"
        height={`${VIEWER_HEIGHT}px`}
        style={{ border: "none", display: "block" }}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        referrerPolicy="no-referrer"
      />
      {/* <OverlayButton
        label={fallbackLabel}
        icon={
          fallbackLabel === "Open PDF" ? <OpenInNewIcon /> : <DownloadIcon />
        }
        onClick={onFallback}
      /> */}
    </Box>
  );
}


export default function KnowledgehubFileViewer({ file, onClose }: Props) {
  const config = getFileConfig(file?.fileType ?? "");
  const [fetchState, setFetchState] = useState<FetchState>({ status: "idle" });
  const abortRef = useRef<AbortController | null>(null);

  const fetchSignedUrl = useCallback(async () => {
    if (!file?.id) return;

    // Cancel any in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setFetchState({ status: "loading" });

    try {
      const response = await KnowledgehubAgent.getSignedUrl(file.id);
      const url = response.message ?? null;

      if (!url) {
        setFetchState({
          status: "error",
          message: "File URL could not be retrieved.",
        });
        return;
      }

      setFetchState({ status: "success", url });
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;

      const message =
        err instanceof Error ? err.message : "File isn't accessible.";

      setFetchState({ status: "error", message });
      toast.error("File isn't accessible.", {
        toastId: "FetchSignedUrlException",
      });
    }
  }, [file?.id]);

  useEffect(() => {
    if (!file?.id) {
      setFetchState({ status: "idle" });
      return;
    }

    fetchSignedUrl();

    return () => {
      abortRef.current?.abort();
      setFetchState({ status: "idle" });
    };
  }, [file?.id, fetchSignedUrl]);

  const handleDownload = useCallback(async () => {
    if (fetchState.status !== "success" || !file) return;
    try {
      const anchor = document.createElement("a");
      anchor.href = fetchState.url;
      anchor.download = file.title;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch {
      toast.error("Failed to download file.");
    }
  }, [fetchState, file]);

  const handleOpenInNewTab = useCallback(() => {
    if (fetchState.status !== "success") return;
    window.open(fetchState.url, "_blank", "noopener,noreferrer");
  }, [fetchState]);


  const renderContent = () => {
    if (fetchState.status === "loading" || fetchState.status === "idle") {
      return <LoadingState />;
    }

    if (fetchState.status === "error") {
      return (
        <ErrorState message={fetchState.message} onRetry={fetchSignedUrl} />
      );
    }

    const { url: signedUrl } = fetchState;
    const fileType = file?.fileType;

    if (fileType === "PDF") {
      return (
        <GoogleDocsViewer
          signedUrl={signedUrl}
          title={file?.title ?? "PDF Viewer"}
          fallbackLabel="Open PDF"
          onFallback={handleOpenInNewTab}
        />
      );
    }

    if (fileType === "MP4") {
      return (
        <video
          src={signedUrl}
          controls
          autoPlay
          muted
          playsInline
          aria-label={file?.title ?? "Video"}
          style={{ width: "100%", maxHeight: `${VIEWER_HEIGHT}px`, display: "block" }}
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    if (fileType === "WORD" || fileType === "PPT") {
      return (
        <GoogleDocsViewer
          signedUrl={signedUrl}
          title={file?.title ?? "Document Viewer"}
          fallbackLabel="Download"
          onFallback={handleDownload}
        />
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          height: VIEWER_HEIGHT,
          bgcolor: "#000",
        }}
      >
        <Typography color="grey.400" variant="body2">
          Preview is not available for this file type.
        </Typography>
        {SUPPORTED_GOOGLE_VIEWER_TYPES.has(fileType ?? "") ? null : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              color: "grey.300",
              borderColor: "grey.700",
              "&:hover": {
                borderColor: "grey.400",
                bgcolor: alpha("#fff", 0.05),
              },
            }}
          >
            Download File
          </Button>
        )}
      </Box>
    );
  };


  return (
    <Dialog
      open={!!file}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      aria-labelledby="file-viewer-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "background.paper",
        },
      }}
    >
      <DialogTitle
        id="file-viewer-title"
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Left: icon + title + badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {file && (
            <Box
              aria-hidden="true"
              sx={{ color: config.color, display: "flex", flexShrink: 0 }}
            >
              {config.icon}
            </Box>
          )}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            noWrap
            title={file?.title}
          >
            {file?.title}
          </Typography>
          {file?.fileType && (
            <Chip
              label={file.fileType}
              size="small"
              sx={{
                bgcolor: alpha(config.color, 0.1),
                color: config.color,
                fontWeight: 600,
                fontSize: "0.65rem",
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Right: action buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
          {/* {fetchState.status === "success" && (
            <>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  aria-label="Download file"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  onClick={handleOpenInNewTab}
                  aria-label="Open file in new tab"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )} */}
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} aria-label="Close viewer">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
